import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useApi } from '../../hooks/useApi';
import VideoDownload from './VideoDownload';

// Whitelist of allowed video MIME types for security
const ALLOWED_VIDEO_TYPES = {
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mkv': 'video/x-matroska',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
};

/**
 * Get safe MIME type from URL
 * Only returns whitelisted video types to prevent XSS
 */
const getSafeVideoType = (url) => {
  if (!url || typeof url !== 'string') {
    return 'video/mp4'; // Default fallback
  }

  // Extract extension from URL (handle query params)
  const urlPath = url.split('?')[0].toLowerCase();

  for (const [ext, mimeType] of Object.entries(ALLOWED_VIDEO_TYPES)) {
    if (urlPath.endsWith(ext)) {
      return mimeType;
    }
  }

  // Default to mp4 if extension not recognized
  return 'video/mp4';
};

const S3VideoPlayer = ({ videoUrl, title, onClose }) => {
  const videoRef = useRef(null);
  const hasFetched = useRef(false);
  const api = useApi();

  useEffect(() => {
    // Prevent duplicate fetches (React strict mode, HMR, etc.)
    if (hasFetched.current || !videoUrl) return;
    hasFetched.current = true;

    const fetchSignedUrl = async () => {
      try {
        const data = await api.post('/api/recordings/presign-url', { key: videoUrl });
        if (videoRef.current && data.signedUrl) {
          // Use whitelist-based MIME type detection for security
          videoRef.current.type = getSafeVideoType(data.signedUrl);
          videoRef.current.src = data.signedUrl;
          videoRef.current.load();
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
        if (videoRef.current) {
          videoRef.current.src = '';
        }
      }
    };

    fetchSignedUrl();
  }, [videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', textAlign: 'center', p: 2, position: 'relative' }}>
      <VideoDownload videoId="muzie-rtc-video-player" />
      {onClose && (
        <IconButton
          sx={{ position: 'absolute', top: 8, right: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <video
          id="muzie-rtc-video-player"
          ref={videoRef}
          width="100%"
          controls
        />
      </Box>
    </Box>
  );
};

export default S3VideoPlayer;
